// The studio's fixes, made on the plan ON SCREEN and judged by the one judge (planVerdict):
// a card's own fix, Fix vertical blocking, the in-block flow sort, and Fix all, which works
// them in turn until none makes the plan better, in a few orders, and keeps the best end.
// Every fix is a stamped plan the caller can adopt as it is, so a fix found on a dry run is
// the fix the click makes.
import type { Pog } from "@CommonTypes/merchflow/pog/pog";
import { PlanogramItem } from "@CommonTypes/merchflow/pog/item";
import {
  pogToStudioItems,
  type PogAdapterOptions,
} from "@CommonUtils/cdtStudio/adapters/pogToStudioItems";
import type { AestheticsRules } from "@CommonUtils/cdtStudio/domain/aestheticsRules";
import { attachResolver, type ResolverPin } from "../resolver/cdtResolve";
import { detectExceptions } from "../resolver/detectExceptions";
import { shelfLayoutItemsOf } from "./shelfRankSpace";
import { resolvedLayout } from "../resolver/resolvedLayout";
import {
  RESOLVER_FLAG_KINDS,
  flagToPin,
  placementId,
  type ResolverInputs,
} from "../resolver/toResolverInputs";
import { resolvePinOn } from "../resolver/resolvePin";
import { blockingFlagsOnly } from "../store/useStudioSession";
import { buildLayout } from "./buildLayout";
import {
  fillShortRows,
  SHELVES_FULL_GATE,
  shortRowsOf,
  type FillPlan,
  type RowFill,
  type ShortRowReason,
} from "./fillShortRows";
import { flowBlocksOf, flowSortedLayout, tilePathsOf } from "./flowSort";
import {
  blockGatherings,
  lanesOf,
  piecesOn,
  readChildrenAlike,
  slidePieceAlongShelf,
  widestOf,
} from "./gatherBlock";
import { namesNoBlock } from "./plainNames";
import {
  fitsWhereMoved,
  holdsAgainst,
  holdsOnTile,
  improvesOn,
  raisesTile,
  stampLayout,
  verdictOfLayout,
  verdictOfPog,
  type PlanVerdict,
} from "./planVerdict";
import { applyFix } from "./studioTransactions";
import {
  type Flag,
  type GeneratedLayout,
  type StudioDraft,
  type StudioItem,
  type StudioRunGeometry,
} from "./studioTypes";
import {
  facingChangesOf,
  fixVerticalBlocking,
  layoutFromItems,
  slabBlockKeyOf,
  type FixVerticalBlockingOptions,
  type VerticalBlockingFix,
} from "./verticalBlockingFix";

export interface FixSetting {
  /** The plan the studio opened on: every fix is stamped over it, as the panel stamps. */
  basePog: Pog;
  runs: readonly StudioRunGeometry[];
  draft: StudioDraft;
  adapterOptions?: PogAdapterOptions;
  /** The detector's options, as the session raises the cards the panel lists. */
  detection: Omit<Parameters<typeof detectExceptions>[3], "gradientPins">;
  /** The order the tree walks the products in (the session layout's walk order). */
  walkOrder?: readonly string[];
  vertical: Omit<FixVerticalBlockingOptions, "rules"> & { rules?: Partial<AestheticsRules> };
  /** On a store plan, each product's minimum days of supply as its pogger request sent it
   *  (`sentMinDaysFromRequest`); null or absent where none was sent (a base plan), and the pog's
   *  own minimum stands. */
  sentMinDays?: ReadonlyMap<string, number | null> | null;
}

/** The draft's record of the decisions made on cards and on placement rules, the rules a Keep
 *  writes, and the brand shelves choice Finalize ships: no fix reads them, and the layout models
 *  no rule. */
const DECISIONS: ReadonlySet<string> = new Set([
  "dismissedFlagIds",
  "acceptedFlagIds",
  "appliedFixFlagIds",
  "restrictions",
  "placementRuleDecisions",
  "keptPlacementOffers",
  "brandShelves",
]);

/** The two drafts lay the same tree: they differ at most in the decisions recorded on cards,
 *  so a fix found with one stands with the other. */
export const sameTreeForFixes = (a: StudioDraft, b: StudioDraft): boolean =>
  [...new Set([...Object.keys(a), ...Object.keys(b)])].every(
    (key) => DECISIONS.has(key) || a[key as keyof StudioDraft] === b[key as keyof StudioDraft],
  );

export interface PlanReading {
  pog: Pog;
  items: readonly StudioItem[];
  /** The cards the panel lists for this plan. */
  flags: readonly Flag[];
  inputs: ResolverInputs;
  verdict: PlanVerdict;
}

export interface PlanFix {
  pog: Pog;
  verdict: PlanVerdict;
}

// A plan is graded and read once: a card's fix is read again to see what it clears, Fix all
// reads every plan it keeps, and the panel reads the plan on screen. Plans are never changed
// in place (a fix stamps a new one), so the plan itself is the key.
const verdicts = new WeakMap<Pog, PlanVerdict>();
const verdictOf = (pog: Pog): PlanVerdict => {
  let verdict = verdicts.get(pog);
  if (!verdict) verdicts.set(pog, (verdict = verdictOfPog(pog)));
  return verdict;
};
const readings = new WeakMap<FixSetting, WeakMap<Pog, PlanReading>>();

export const readPlan = (setting: FixSetting, pog: Pog): PlanReading => {
  let read = readings.get(setting);
  if (!read) readings.set(setting, (read = new WeakMap()));
  const known = read.get(pog);
  if (known) return known;
  const { items, runs } = pogToStudioItems([pog], setting.adapterOptions);
  const detected = detectExceptions(items, runs, setting.draft, setting.detection);
  const reading = {
    pog,
    items,
    flags: blockingFlagsOnly(detected.flags),
    inputs: detected.inputs,
    verdict: verdictOf(pog),
  };
  read.set(pog, reading);
  return reading;
};

/** A layout of the plan's own products stamped the way the panel stamps its plans. */
const stamp = (
  setting: FixSetting,
  reading: PlanReading,
  layout: GeneratedLayout,
  unranged: readonly string[] = [],
): Pog => stampLayout(setting.basePog, layout, reading.items, unranged);

/** The resolver's repair of the pin, made on this plan, or null when it moves nothing. */
const resolverRepairWith = (
  reading: PlanReading,
  pin: ResolverPin,
  kind: string,
): GeneratedLayout | null => {
  try {
    const studio = attachResolver(reading.inputs.canvas, reading.inputs.itemsDoc, {
      runW: reading.inputs.runW,
      ...(reading.inputs.sizeRule ? { sizeRule: reading.inputs.sizeRule } : {}),
      ...(reading.inputs.rankOfShelf ? { rankOfShelf: reading.inputs.rankOfShelf } : {}),
    });
    const step = resolvePinOn(studio, pin, kind);
    return step.physicalChange
      ? resolvedLayout(reading.items, studio.positions(), reading.inputs)
      : null;
  } catch {
    return null;
  }
};

/** The resolver's own repair of the card, made on this plan, or null when it moves nothing. */
const resolverRepairOf = (reading: PlanReading, flag: Flag): GeneratedLayout | null =>
  RESOLVER_FLAG_KINDS.has(flag.kind)
    ? resolverRepairWith(
        reading,
        reading.inputs.pinByFlagId[flag.id] ?? flagToPin(flag, reading.items),
        flag.kind,
      )
    : null;

/** Every product keeps the facings it has; hanging products are never in a layout. */
const keepsFacings = (items: readonly StudioItem[], layout: GeneratedLayout): boolean =>
  shelfLayoutItemsOf(items).every(
    (item) =>
      (layout.placements[item.skuId]?.segments ?? []).reduce(
        (sum, segment) => sum + segment.hFacings,
        0,
      ) === item.facings,
  );

/** The plan after clears the card and raises none the plan before did not: the card is gone,
 *  no card is new, and fewer are left. */
const clearsCard = (before: readonly Flag[], after: readonly Flag[], flag: Flag): boolean => {
  const known = new Set(before.map((card) => card.id));
  return (
    after.length < before.length && after.every((card) => card.id !== flag.id && known.has(card.id))
  );
};

/** Kinds whose card reads a block's order rather than its shape. */
const ORDER_KINDS: ReadonlySet<Flag["kind"]> = new Set([
  "inversion",
  "size-order",
  "he-order",
  "flavour-order",
]);

// A card's repairs are found once for each reading: its fix and Fix all's step both read them.
const cardRepairs = new WeakMap<PlanReading, Map<Flag, CardRepairs>>();

interface CardRepairs {
  fixes: PlanFix[];
  /** Why the card has none, where a repair that would have stood cut a product (`CutRefusal`). */
  refusal: CutRefusal | null;
}

/** The card's harmless repairs, best first. One that cuts a product below its days of supply is
 *  refused even at the same score (`cutRefusalOf`). */
const cardRepairsOf = (setting: FixSetting, reading: PlanReading, flag: Flag): CardRepairs => {
  let byFlag = cardRepairs.get(reading);
  if (!byFlag) cardRepairs.set(reading, (byFlag = new Map()));
  let found = byFlag.get(flag);
  if (!found) byFlag.set(flag, (found = findCardRepairs(setting, reading, flag)));
  return found;
};

/** Why a step that cuts a product's facings is refused: it takes the product below its days of
 *  supply, or off the plan (Autofix refuses that as a dropped product). */
export type CutRefusal = "belowDos";

/** What one product holds on a plan, as Autofix measures it (owa-pog-agent `acceptance._stock`):
 *  its facing columns, its units on shelf (facings x depth x units per pack, z-capped loose units
 *  left out), its weekly sales, and the pog's own minimum days of supply (its first placement's). */
interface ProductStock {
  facings: number;
  units: number;
  weekly: number;
  minDays: number;
}

const stockByPog = new WeakMap<Pog, ReadonlyMap<string, ProductStock>>();
const stockOf = (pog: Pog): ReadonlyMap<string, ProductStock> => {
  const known = stockByPog.get(pog);
  if (known) return known;
  const stock = new Map<string, ProductStock>();
  for (const bay of pog.planogram.bays) {
    for (const shelf of bay.shelves) {
      for (const item of shelf.items) {
        if (!(item instanceof PlanogramItem)) continue;
        const skuId = item.productCode || item.id;
        const entry = stock.get(skuId) ?? {
          facings: 0,
          units: 0,
          weekly: 0,
          minDays: item.minDaysOfSupply,
        };
        entry.facings += item.getHorizontalFacings();
        entry.units += item.facings * item.getDepthFacings() * item.getUnits();
        entry.weekly = Number(item.quantity) || 0;
        stock.set(skuId, entry);
      }
    }
  }
  stockByPog.set(pog, stock);
  return stock;
};

/** Days of supply as Autofix reads them: units over weekly sales, times 7. */
const daysOf = ({ units, weekly }: ProductStock) => (units / weekly) * 7;

/**
 * A fix may take facings from a product (Rule A), never below its days of supply, as Autofix
 * holds it: a step is refused where some product ends with fewer facings and either its days of
 * supply after is under its minimum, or it was under it already, or it leaves the plan. One
 * rule for every step the studio offers or Fix all keeps: a card's repair, a block's pieces
 * moved, the squaring, the flow sort.
 *
 * Judged as the agent judges it (DEV-3672): the minimum is the store's request's where one was
 * sent (`FixSetting.sentMinDays`, as `bounds_from_request`), else the pog's own; a minimum
 * neither gives is 0. A product with no sales, or none on shelf, has no days of supply a cut
 * could take under a minimum, so it may be cut (the agent skips it too).
 */
const cutRefusalOf = (setting: FixSetting, before: PlanReading, after: Pog): CutRefusal | null => {
  const was = stockOf(before.pog);
  const now = stockOf(after);
  const sent = setting.sentMinDays;
  for (const [skuId, stock] of was) {
    const left = now.get(skuId);
    if (left && left.facings >= stock.facings) continue;
    if (!left || left.facings <= 0) return "belowDos";
    if (!(stock.weekly > 0) || !(stock.units > 0)) continue;
    const sentMin = sent ? sent.get(skuId) : stock.minDays;
    const min = Number.isFinite(sentMin) ? (sentMin as number) : 0;
    if (daysOf(stock) < min || daysOf(left) < min) return "belowDos";
  }
  return null;
};

const findCardRepairs = (setting: FixSetting, reading: PlanReading, flag: Flag): CardRepairs => {
  const layouts: GeneratedLayout[] = [];
  const repaired = resolverRepairOf(reading, flag);
  if (repaired) layouts.push(repaired);
  layouts.push(
    ...blockGatherings(reading.items, setting.runs, setting.draft, flag, setting.walkOrder),
  );
  if (ORDER_KINDS.has(flag.kind)) {
    const sorted = flowSortedLayout(reading.pog, reading.items, flag.skuIds);
    if (sorted) layouts.push(sorted);
  }
  const standing = layouts
    .map((layout) => {
      const pog = stamp(setting, reading, layout);
      return {
        pog,
        kept: keepsFacings(reading.items, layout),
        refusal: cutRefusalOf(setting, reading, pog),
      };
    })
    .filter(({ pog }) => fitsWhereMoved(reading.pog, pog))
    .map((fix) => ({ ...fix, verdict: verdictOf(fix.pog) }))
    .filter((fix) => holdsAgainst(fix.verdict, reading.verdict));
  const fixes = standing
    .filter((fix) => fix.refusal === null)
    .sort((a, b) => b.verdict.exactScore - a.verdict.exactScore || Number(b.kept) - Number(a.kept))
    .map(({ pog, verdict }) => ({ pog, verdict }));
  const refusal =
    fixes.length === 0 ? (standing.find((fix) => fix.refusal)?.refusal ?? null) : null;
  return { fixes, refusal };
};

/** Why the card has no fix, where the studio can say more than that none raises the score: the
 *  only repairs it found cut a product below its days of supply, or one it cannot judge. */
export const cardRefusalOf = (
  setting: FixSetting,
  reading: PlanReading,
  flag: Flag,
): CutRefusal | null => cardRepairsOf(setting, reading, flag).refusal;

/** The card's fix on this plan: its best repair, offered only where it raises the score the
 *  tile shows (`raisesTile`): a click must change something the user can see. Null otherwise. */
export const cardFixOf = (setting: FixSetting, reading: PlanReading, flag: Flag): PlanFix | null =>
  cardRepairsOf(setting, reading, flag).fixes.find((fix) =>
    raisesTile(fix.verdict, reading.verdict),
  ) ?? null;

/** A step Fix all may take on the card: a repair that raises the score, even under the tile's
 *  rounding, or clears the card at a score no lower before rounding, with no other in its place
 *  (two cards may not hand a pack back and forth). A path to a higher tile, never an end (see `runFixes`). */
export const cardStepOf = (
  setting: FixSetting,
  reading: PlanReading,
  flag: Flag,
): PlanFix | null => {
  const holding = cardRepairsOf(setting, reading, flag).fixes;
  return (
    holding.find((fix) => improvesOn(fix.verdict, reading.verdict)) ??
    holding.find((fix) => clearsCard(reading.flags, readPlan(setting, fix.pog).flags, flag)) ??
    null
  );
};

/**
 * The card's change to the tree, as its Fix button makes it: the draft it leaves when the plan
 * the tree then lays raises the score the tile shows (`raisesTile`), else null. Judged before
 * the button is shown, so a card never offers a click that would be refused, nor one that
 * gains nothing on screen; a drag may lower the score, once the user agrees to the cost.
 */
export const draftFixOf = (
  setting: FixSetting,
  reading: PlanReading,
  flag: Flag,
): StudioDraft | null => {
  if (!flag.fixLabel) return null;
  const next = applyFix(setting.draft, flag);
  const laid = verdictOfLayout(
    setting.basePog,
    buildLayout(reading.items, next, setting.runs),
    reading.items,
  );
  return raisesTile(laid, reading.verdict) ? next : null;
};

export interface VerticalOffer {
  result: VerticalBlockingFix;
  /** The squared plan, offered only where it stands every pack where it fits and raises the
   *  score the tile shows; null otherwise, and where the shelves are too sparse or nothing moves. */
  fix: PlanFix | null;
  /** The squared plan as judged, offered or not, so a refusal can say why. */
  squared: { verdict: PlanVerdict; fits: boolean } | null;
  /** The squaring would cut a product past `cutRefusalOf`: refused, and why. */
  cutRefusal: CutRefusal | null;
}

// What a plan offers is found once for each reading (one per setting and plan, see `readPlan`):
// the panel looks for it on the plan on screen, and Fix all looks again on the plans it works.
const verticalOffers = new WeakMap<PlanReading, VerticalOffer>();
const flowFixes = new WeakMap<PlanReading, PlanFix | null>();
const flowCardsByBlock = new WeakMap<PlanReading, Map<string, FixedCard | null>>();
const pieceCardsByBlock = new WeakMap<PlanReading, Map<string, FixedCard | null>>();

/** Fix vertical blocking on this plan. The profile's "only if better" steers the squaring
 *  itself; the offer's bar is the studio's, as for every other fix. */
export const verticalOfferOf = (setting: FixSetting, reading: PlanReading): VerticalOffer => {
  const known = verticalOffers.get(reading);
  if (known) return known;
  const offer = findVerticalOffer(setting, reading);
  verticalOffers.set(reading, offer);
  return offer;
};

/** Most passes of the squaring one Fix vertical blocking makes. */
const MAX_SQUARINGS = 4;

/** The squaring of this plan; `moved` is false where it is refused or moves nothing. */
const squaringOf = (setting: FixSetting, reading: PlanReading) => {
  const source = layoutFromItems(reading.items);
  const result = fixVerticalBlocking(
    reading.items,
    source,
    setting.runs,
    setting.draft,
    setting.vertical,
  );
  return { result, moved: !result.refusedSparse && result.layout !== source };
};

/**
 * The plan squared, and squared again while each pass raises the score: a pass evens the blocks
 * as it found them, so the next can square what the last one left (toilet paper squared after
 * its cards fell from 80 to 79; squared again, it rose to 83). The result reports the passes
 * kept together, against the plan as it was.
 */
const findVerticalOffer = (setting: FixSetting, reading: PlanReading): VerticalOffer => {
  const first = squaringOf(setting, reading);
  if (!first.moved) return { result: first.result, fix: null, squared: null, cutRefusal: null };
  const unranged = new Set(first.result.unranged);
  let last = {
    result: first.result,
    pog: stamp(setting, reading, first.result.layout, [...unranged]),
  };
  const cutRefusal = cutRefusalOf(setting, reading, last.pog);
  if (cutRefusal) return { result: first.result, fix: null, squared: null, cutRefusal };
  let best = { ...last, verdict: verdictOf(last.pog) };
  for (let pass = 1; pass < MAX_SQUARINGS; pass += 1) {
    const squaredOnce = readPlan(setting, last.pog);
    const again = squaringOf(setting, squaredOnce);
    if (!again.moved) break;
    const passUnranged = new Set([...unranged, ...again.result.unranged]);
    const pog = stamp(setting, squaredOnce, again.result.layout, [...passUnranged]);
    // A pass that would cut a product past its days of supply is not made.
    if (cutRefusalOf(setting, squaredOnce, pog)) break;
    passUnranged.forEach((skuId) => unranged.add(skuId));
    last = { result: again.result, pog };
    const verdict = verdictOf(last.pog);
    if (!fitsWhereMoved(reading.pog, last.pog) || !improvesOn(verdict, best.verdict)) break;
    best = { ...last, verdict };
  }
  const result =
    best.result === first.result
      ? first.result
      : {
          ...best.result,
          ...facingChangesOf(
            reading.items,
            layoutFromItems(reading.items),
            best.result.layout,
            unranged,
          ),
          unranged: [...unranged],
          raggedBefore: first.result.raggedBefore,
          scoreBefore: first.result.scoreBefore,
        };
  const squared = { verdict: best.verdict, fits: fitsWhereMoved(reading.pog, best.pog) };
  const offered = squared.fits && raisesTile(squared.verdict, reading.verdict);
  return {
    result,
    fix: offered ? { pog: best.pog, verdict: squared.verdict } : null,
    squared,
    cutRefusal: null,
  };
};

/** Every block's products in flow order, or null when every block already reads in order. */
export const flowFixOf = (setting: FixSetting, reading: PlanReading): PlanFix | null => {
  if (flowFixes.has(reading)) return flowFixes.get(reading) ?? null;
  const sorted = flowSortedLayout(reading.pog, reading.items);
  const pog = sorted && stamp(setting, reading, sorted);
  const fix =
    pog && fitsWhereMoved(reading.pog, pog) && !cutRefusalOf(setting, reading, pog)
      ? { pog, verdict: verdictOf(pog) }
      : null;
  flowFixes.set(reading, fix);
  return fix;
};

/** A card the studio raises from the tile's own reading of the plan, with its fix. */
export interface RaisedCard {
  flag: Flag;
  /** Null for a card that says why nothing here can fix it (the "Shelves full" card). */
  fix: PlanFix | null;
}

/** A raised card that is raised only with its fix. */
export interface FixedCard extends RaisedCard {
  fix: PlanFix;
}

export const FLOW_CARD_PREFIX = "flow:";

/**
 * The top-level blocks whose flow the tile marks down and the flow sort puts right: one card
 * each, raised only where sorting the block raises the score the tile shows, so every one can
 * be fixed.
 * Named by the block ("Cottonelle out of order").
 */
export const flowCardsOf = (setting: FixSetting, reading: PlanReading): FixedCard[] =>
  [...flowBlocksOf(reading.pog)].flatMap(([block, skuIds]) => {
    const card = flowCardOf(setting, reading, block, skuIds);
    return card ? [card] : [];
  });

/** The block's card, or null where sorting it does not raise the tile. */
const flowCardOf = (
  setting: FixSetting,
  reading: PlanReading,
  block: string,
  skuIds: string[],
): FixedCard | null => {
  let cards = flowCardsByBlock.get(reading);
  if (!cards) flowCardsByBlock.set(reading, (cards = new Map()));
  if (!cards.has(block)) cards.set(block, findFlowCard(setting, reading, block, skuIds));
  return cards.get(block) ?? null;
};

const findFlowCard = (
  setting: FixSetting,
  reading: PlanReading,
  block: string,
  skuIds: string[],
): FixedCard | null => {
  const sorted = flowSortedLayout(reading.pog, reading.items, skuIds);
  if (!sorted) return null;
  const pog = stamp(setting, reading, sorted);
  if (!fitsWhereMoved(reading.pog, pog) || cutRefusalOf(setting, reading, pog)) return null;
  const verdict = verdictOf(pog);
  if (!raisesTile(verdict, reading.verdict)) return null;
  return {
    flag: {
      id: `${FLOW_CARD_PREFIX}${block}`,
      kind: "he-order",
      severity: "toCheck",
      scopePath: [block],
      // The panel words the card from its locale (resolver.flowCard*).
      title: block,
      detail: "",
      skuIds,
      anchorSkuId: skuIds[0],
      evidence: {},
      fix: null,
      fixLabel: null,
    },
    fix: { pog, verdict },
  };
};

const PIECES_CARD_PREFIX = "pieces:";

/** What a block-in-pieces card says, for the panel to word from its locale: the block's own
 *  name, its pieces, and the shelves the part that stands apart is on. */
export interface PiecesCardWords {
  block: string;
  pieces: number;
  shelves: readonly number[];
}
const piecesCardWords = new WeakMap<Flag, PiecesCardWords>();
export const piecesCardWordsOf = (flag: Flag): PiecesCardWords | null =>
  piecesCardWords.get(flag) ?? null;

/**
 * The blocks the tile counts in pieces that no card names: one card each, whose fix makes the
 * block whole by the best of moving every piece but the widest beside it, as the resolver moves
 * a stray (a top-level block), or sliding one piece along its shelf to stand with the rest,
 * among its parent's products where it has a parent. A brand can stand in pieces with each of
 * its sub-blocks whole, and no detector card names that: live, squaring Womens deodorant first
 * left Secret? on shelf 1 and on shelves 3 and 4 with nothing to click. Raised only where the
 * move raises the score the tile shows. The blocks are the tile's own ("Puffs › Plus Lotion").
 */
export const pieceCardsOf = (setting: FixSetting, reading: PlanReading): FixedCard[] =>
  namedInPiecesOf(reading).flatMap((block) => {
    const card = pieceCardOf(setting, reading, block);
    return card ? [card] : [];
  });

/** The first of those cards, its blocks read in turn until one gives a card: Fix all takes
 *  one at a time, so the rest are not worked out. */
const firstPieceCardOf = (setting: FixSetting, reading: PlanReading): FixedCard | null => {
  for (const block of namedInPiecesOf(reading)) {
    const card = pieceCardOf(setting, reading, block);
    if (card) return card;
  }
  return null;
};

const namedInPiecesOf = (reading: PlanReading): readonly string[] =>
  reading.verdict.blocksNamedInPieces?.() ?? [];

/** The block's card, or null where no move of its pieces raises the tile. */
const pieceCardOf = (setting: FixSetting, reading: PlanReading, block: string) => {
  let cards = pieceCardsByBlock.get(reading);
  if (!cards) pieceCardsByBlock.set(reading, (cards = new Map()));
  if (!cards.has(block)) cards.set(block, findPieceCard(setting, reading, block));
  return cards.get(block) ?? null;
};

/** What every block's card reads of the plan, read once for each reading. */
const pieceContexts = new WeakMap<PlanReading, ReturnType<typeof pieceContextOf>>();
const pieceContextOf = (reading: PlanReading) => {
  const tilePaths = tilePathsOf(reading.pog);
  return {
    named: new Set(reading.flags.flatMap((flag) => flag.skuIds)),
    lanes: lanesOf(reading.items),
    packWidth: new Map(reading.items.map((item) => [item.skuId, item.facingWidthCm])),
    tilePaths,
    startsWith: (skuId: string, path: readonly string[]) =>
      path.every((value, depth) => tilePaths.get(skuId)?.[depth] === value),
  };
};

const findPieceCard = (
  setting: FixSetting,
  reading: PlanReading,
  block: string,
): FixedCard | null => {
  let context = pieceContexts.get(reading);
  if (!context) pieceContexts.set(reading, (context = pieceContextOf(reading)));
  const { named, lanes, packWidth, tilePaths, startsWith } = context;
  const path = block.split(" › ");
  // A block a placeholder names ("Unassigned CDT1") is no block a user would gather.
  if (namesNoBlock(path[path.length - 1])) return null;
  const parent = path.slice(0, -1);
  const isMember = (skuId: string) => startsWith(skuId, path);
  const pieces = setting.runs.flatMap((run) =>
    piecesOn(lanes, run, isMember, (skuId) => packWidth.get(skuId)),
  );
  if (pieces.length < 2) return null;
  const moves = slidePieceAlongShelf(reading.items, setting.runs, isMember, (skuId) =>
    startsWith(skuId, parent) ? tilePaths.get(skuId)?.[parent.length] : undefined,
  ).map(({ layout, piece }) => ({ layout, stray: piece }));
  const stray = pieces.filter((piece) => piece !== widestOf(pieces)).flat();
  if (parent.length === 0) {
    const ids = stray.map((position) => placementId(position.skuId, position.index));
    const layout = resolverRepairWith(
      reading,
      { kind: "orphan", scope: block, node: block, ids },
      "orphan",
    );
    if (layout) moves.push({ layout, stray });
  } else {
    // Its parent's children read alike on every shelf.
    const childOf = new Map(
      reading.items
        .filter((item) => startsWith(item.skuId, parent))
        .map((item) => [item.skuId, tilePaths.get(item.skuId)?.[parent.length] ?? ""]),
    );
    moves.push(
      ...readChildrenAlike(reading.items, childOf, [], true).map((layout) => ({ layout, stray })),
    );
  }
  const [best] = moves
    .filter(({ stray }) => !stray.some((position) => named.has(position.skuId)))
    .map(({ layout, stray }) => ({ pog: stamp(setting, reading, layout), stray }))
    .filter(({ pog }) => fitsWhereMoved(reading.pog, pog) && !cutRefusalOf(setting, reading, pog))
    .map((move) => ({ ...move, verdict: verdictOf(move.pog) }))
    .filter(({ verdict }) => raisesTile(verdict, reading.verdict))
    .sort((a, b) => b.verdict.exactScore - a.verdict.exactScore);
  if (!best) return null;
  const skuIds = [...new Set(best.stray.map((position) => position.skuId))];
  const shelves = [...new Set(best.stray.map((position) => position.shelf))].sort((a, b) => a - b);
  const where = shelves.length > 1 ? `shelves ${shelves.join(", ")}` : `shelf ${shelves[0]}`;
  // The title and detail are the card's record (a Keep writes the title on its rule); the
  // panel words the card from its locale (resolver.piecesCard*).
  const flag: Flag = {
    id: `${PIECES_CARD_PREFIX}${block}`,
    kind: "split",
    severity: "blocking",
    scopePath: path,
    title: `${block} split into ${pieces.length} blocks`,
    detail: `Part of the block stands apart on ${where}. Move it beside the rest so it reads as one block.`,
    skuIds,
    anchorSkuId: skuIds[0],
    evidence: {},
    fix: null,
    fixLabel: null,
  };
  piecesCardWords.set(flag, { block: path[path.length - 1], pieces: pieces.length, shelves });
  return { flag, fix: { pog: best.pog, verdict: best.verdict } };
};

// A plan's short rows are filled once for each reading: the card offers the fill, and Fix all
// takes it as a step.
const rowFills = new WeakMap<PlanReading, { fill: RowFill; fix: PlanFix | null }>();

/** A plan as the row fill reads it: its products as drawn and its verdict, without the cards. */
const fillPlanOf = (setting: FixSetting, pog: Pog): FillPlan => ({
  pog,
  items: pogToStudioItems([pog], setting.adapterOptions).items,
  verdict: verdictOf(pog),
});

/**
 * The plan's short rows filled (`fillShortRows`): a facing at a time, on each short row one
 * product per block, the one that runs out first, judged on the whole plan. The fill is a fix
 * where it betters the plan, or clears the "Shelves full" check at no harm: while another check
 * fails, the score the tile shows can hold still as the shelves fill.
 */
const rowFillOf = (setting: FixSetting, reading: PlanReading) => {
  const known = rowFills.get(reading);
  if (known) return known;
  const itemOf = new Map(reading.items.map((item) => [item.skuId, item]));
  const fill = fillShortRows(reading, {
    runs: setting.runs,
    read: (pog) => fillPlanOf(setting, pog),
    stamp: (layout, items) => stampLayout(setting.basePog, layout, items),
    blockOf: (skuId) => {
      const item = itemOf.get(skuId);
      return item ? slabBlockKeyOf(setting.draft, item) : skuId;
    },
    // Each facing is judged on the tile, the fill's end on the exact score below: a facing
    // a row needs may cost a hair the facings after it win back (toilet paper's shelf 2).
    holds: holdsOnTile,
  });
  const end = fill.end.verdict;
  const fix =
    fill.grown.length > 0 &&
    (improvesOn(end, reading.verdict) ||
      (holdsAgainst(end, reading.verdict) && !end.failedGates.includes(SHELVES_FULL_GATE)))
      ? { pog: fill.end.pog, verdict: end }
      : null;
  const entry = { fill, fix };
  rowFills.set(reading, entry);
  return entry;
};

/** The plan with its short rows filled, or null where nothing can be added (see `rowFillOf`). */
export const fillFixOf = (setting: FixSetting, reading: PlanReading): PlanFix | null =>
  rowFillOf(setting, reading).fix;

export const FILL_CARD_ID = "fill:shelves";

/** What the "Shelves full" card says, for the panel to word from its locale: the shelves with
 *  gaps, and why they cannot be filled here where they cannot. */
export interface FillCardWords {
  shelves: readonly number[];
  reason: ShortRowReason | null;
}
const fillCardWords = new WeakMap<Flag, FillCardWords>();
export const fillCardWordsOf = (flag: Flag): FillCardWords | null =>
  fillCardWords.get(flag) ?? null;

const fillCards = new WeakMap<PlanReading, RaisedCard | null>();

/**
 * The card for the rows the "Shelves full" check fails: its fix adds facings to the products
 * already on them, offered where it raises the score the tile shows or passes the check at no
 * harm. Where it does neither, the card stays without a fix and says why
 * (`FillCardWords.reason`).
 */
export const fillCardOf = (setting: FixSetting, reading: PlanReading): RaisedCard | null => {
  if (fillCards.has(reading)) return fillCards.get(reading) ?? null;
  const card = findFillCard(setting, reading);
  fillCards.set(reading, card);
  return card;
};

const findFillCard = (setting: FixSetting, reading: PlanReading): RaisedCard | null => {
  const maxRowAir = reading.verdict.maxRowAir;
  if (maxRowAir === undefined || !reading.verdict.failedGates.includes(SHELVES_FULL_GATE)) {
    return null;
  }
  const rows = shortRowsOf(reading.items, setting.runs, maxRowAir);
  if (rows.length === 0) return null;
  const { fill, fix } = rowFillOf(setting, reading);
  // The tile shows the gain, or the check passes: while another check fails, the score can
  // hold still as the shelves fill, and the check turning to passed is what the click shows.
  const offered =
    fix &&
    (raisesTile(fix.verdict, reading.verdict) ||
      !fix.verdict.failedGates.includes(SHELVES_FULL_GATE))
      ? fix
      : null;
  const shelves = [...new Set(rows.map((row) => row.shelf))].sort((a, b) => a - b);
  const reason: ShortRowReason | null = offered ? null : (fill.short[0]?.reason ?? "harm");
  const skuIds = reading.items
    .filter((item) =>
      item.placements.some((placement) =>
        rows.some((row) => row.run === placement.run && row.shelf === placement.shelf),
      ),
    )
    .map((item) => item.skuId);
  const where = shelves.length > 1 ? `Shelves ${shelves.join(", ")}` : `Shelf ${shelves[0]}`;
  // The title and detail are the card's record; the panel words the card from its locale
  // (resolver.fillCard*).
  const flag: Flag = {
    id: FILL_CARD_ID,
    kind: "data",
    severity: "blocking",
    scopePath: [],
    title: `${where} not full`,
    detail: "The products on it leave gaps. Add facings to fill it.",
    skuIds,
    anchorSkuId: skuIds[0] ?? "",
    evidence: {},
    fix: null,
    fixLabel: null,
  };
  fillCardWords.set(flag, { shelves, reason });
  return { flag, fix: offered };
};

/** Every card the studio raises from the tile: blocks in pieces no card names, then flow, then
 *  the shelves with gaps. */
export const raisedCardsOf = (setting: FixSetting, reading: PlanReading): RaisedCard[] => {
  const fill = fillCardOf(setting, reading);
  return [
    ...pieceCardsOf(setting, reading),
    ...flowCardsOf(setting, reading),
    ...(fill ? [fill] : []),
  ];
};

/** A fix Fix all kept: a card's own, Fix vertical blocking, the flow sort, or the shelves
 *  filled. */
type KeptFix =
  | { kind: "card"; flag: Flag }
  | { kind: "vertical" }
  | { kind: "flow" }
  | { kind: "fill" };

export interface FixAllResult {
  pog: Pog;
  after: PlanVerdict;
  /** The fixes kept, in the order they were made. */
  kept: readonly KeptFix[];
}

/** Most passes over the kinds; each pass that keeps nothing ends the run sooner. */
const MAX_PASSES = 12;
/** Most fixes one run keeps. Each kept fix ranks strictly above the last, so a run ends well
 *  short of this; it holds only should a bug let a fix be kept that is no better. */
const MAX_KEPT = 500;

type FixKind = "cards" | "vertical" | "flow" | "fill";

interface FixRun {
  reading: PlanReading;
  kept: readonly KeptFix[];
}

/**
 * One greedy run: each pass works the kinds in `order` and starts over at the first kind once
 * one of them keeps a fix, until a pass keeps nothing. The cards go in list order, each card's
 * own fix (`cardFixOf`, which raises the tile) tried before any step that only clears a card,
 * so such a step never stands in the way of a card's fix; the list is read again from the top
 * once one is kept. Fix vertical blocking and the flow sort (block by block, then every block)
 * are kept only when they make the plan better. A card's step is kept when it raises the
 * score, or clears cards at the same score (`cardStepOf`), never by way of harm: the plan never
 * takes a step back, and each kept step ranks strictly above the last, so the run ends. A step
 * at the same tile is a path, not an end: a run whose end does not raise the tile over the
 * start's keeps nothing, so Fix all never ends where the tile shows nothing gained; one whose
 * end does keeps every step, those after the tile last rose too. `MAX_PASSES` bounds the
 * passes, not the cards read again from the top within one: that ends for the same reason, and
 * `MAX_KEPT` stops it should a bug break the ranking. `step` is awaited before every fix tried.
 */
const runFixes = async (
  setting: FixSetting,
  start: PlanReading,
  order: readonly FixKind[],
  step: () => Promise<void>,
): Promise<FixRun> => {
  let reading = start;
  const kept: KeptFix[] = [];
  const take = (next: PlanReading, as: KeptFix) => {
    reading = next;
    kept.push(as);
    return true;
  };
  /** Better than the standing plan: a higher score, or as high with fewer cards. */
  const keep = (fix: PlanFix | null, as: KeptFix, clearingCounts = true): boolean => {
    if (!fix || kept.length >= MAX_KEPT) return false;
    // Every step is held to the days-of-supply rule here too, whatever found it.
    if (cutRefusalOf(setting, reading, fix.pog)) return false;
    const next = readPlan(setting, fix.pog);
    if (improvesOn(fix.verdict, reading.verdict)) return take(next, as);
    if (!clearingCounts || !holdsAgainst(fix.verdict, reading.verdict)) return false;
    return next.flags.length < reading.flags.length && take(next, as);
  };
  const work = async (kind: FixKind): Promise<boolean> => {
    if (kind === "fill") {
      // A fill that clears the "Shelves full" check at no harm is a path: while another check
      // fails, the tile's score can hold still until that check is fixed too.
      await step();
      const fix = fillFixOf(setting, reading);
      if (!fix || kept.length >= MAX_KEPT) return false;
      return (
        (improvesOn(fix.verdict, reading.verdict) ||
          (holdsAgainst(fix.verdict, reading.verdict) &&
            reading.verdict.failedGates.includes(SHELVES_FULL_GATE) &&
            !fix.verdict.failedGates.includes(SHELVES_FULL_GATE))) &&
        take(readPlan(setting, fix.pog), { kind: "fill" })
      );
    }
    if (kind === "vertical") {
      await step();
      return keep(verticalOfferOf(setting, reading).fix, { kind: "vertical" }, false);
    }
    if (kind === "flow") {
      // Each block out of flow sorted on its own first, as its card sorts it: sorting every
      // block at once can be no better where sorting one is, and would leave that card up.
      // Sorting one block leaves every block's products as they were, so the blocks are read
      // once and each is judged on the plan as the sorts before it left it.
      let any = false;
      for (const [block, skuIds] of flowBlocksOf(reading.pog)) {
        await step();
        const card = flowCardOf(setting, reading, block, skuIds);
        if (card && keep(card.fix, { kind: "card", flag: card.flag }, false)) any = true;
      }
      await step();
      return keep(flowFixOf(setting, reading), { kind: "flow" }, false) || any;
    }
    // After a kept fix the list is read again from the top, as a user works down it.
    const keepACard = async (): Promise<boolean> => {
      for (const fixOf of [cardFixOf, cardStepOf]) {
        for (const flag of reading.flags) {
          await step();
          if (keep(fixOf(setting, reading, flag), { kind: "card", flag })) return true;
        }
      }
      return false;
    };
    let any = false;
    while (await keepACard()) any = true;
    // Then a block in pieces that no card names, as the panel lists it after them.
    await step();
    const pieces = firstPieceCardOf(setting, reading);
    if (pieces && keep(pieces.fix, { kind: "card", flag: pieces.flag }, false)) any = true;
    return any;
  };

  for (let pass = 0; pass < MAX_PASSES; pass += 1) {
    let any = false;
    for (const kind of order) {
      if (await work(kind)) {
        any = true;
        break;
      }
    }
    if (!any) break;
  }
  return raisesTile(reading.verdict, start.verdict)
    ? { reading, kept }
    : { reading: start, kept: [] };
};

/** The orders Fix all tries. Greedy in one order, a fix made early can stand in the way of a
 *  better end another order reaches: live, Fix all ended Womens deodorant at 93 where working
 *  it by hand reached 94. Cards first is tried first, and no other order is kept unless it
 *  ends better. */
const FIX_ORDERS: readonly (readonly FixKind[])[] = [
  ["cards", "vertical", "flow", "fill"],
  ["vertical", "cards", "flow", "fill"],
  ["flow", "cards", "vertical", "fill"],
  // Shelves filled first can leave the cards more to gain: kitchen towels ends at 94 this way,
  // 93 filling last.
  ["fill", "cards", "vertical", "flow"],
];

/** A better end: a higher score by the one judge, or as high with fewer cards left, then with
 *  fewer fixes made to get there. */
const endsBetter = (candidate: FixRun, incumbent: FixRun): boolean => {
  const [a, b] = [candidate.reading, incumbent.reading];
  if (improvesOn(a.verdict, b.verdict)) return true;
  if (!holdsAgainst(a.verdict, b.verdict)) return false;
  return (
    a.flags.length < b.flags.length ||
    (a.flags.length === b.flags.length && candidate.kept.length < incumbent.kept.length)
  );
};

/** Fix vertical blocking or the flow sort keeps something on this plan. Where neither does,
 *  the orders that lead with them open as cards first does. */
const leadsElsewhere = (setting: FixSetting, reading: PlanReading): boolean => {
  if (verticalOfferOf(setting, reading).fix) return true;
  if (flowCardsOf(setting, reading).length > 0) return true;
  if (fillFixOf(setting, reading)) return true;
  const sorted = flowFixOf(setting, reading);
  return sorted !== null && improvesOn(sorted.verdict, reading.verdict);
};

/** Each start's run, and the progress of every caller waiting on it. */
const squaredFirstRuns = new WeakMap<
  PlanReading,
  { run: Promise<FixRun | null>; steps: Set<() => Promise<void>> }
>();

/**
 * Fix vertical blocking clicked first on `start`, then the cards, or null where squaring is not
 * offered on it. Squaring once and then the cards can end above squaring again after every
 * card, as the order that leads with it does (live, Womens deodorant 94 against 93), and above
 * fixing the cards first, which can leave less to square (live, Facial Tissue 79 against 72).
 * Worked once for each start: Fix all and Fix vertical blocking both ask for it, and each
 * caller's `step` is taken at every fix tried from when it asks. A run that fails is not kept:
 * the next caller works it again.
 */
const squaredFirstRun = (
  setting: FixSetting,
  start: PlanReading,
  step: () => Promise<void>,
): Promise<FixRun | null> => {
  const known = squaredFirstRuns.get(start);
  if (known) {
    known.steps.add(step);
    return known.run;
  }
  const steps = new Set([step]);
  const squared = verticalOfferOf(setting, start).fix;
  const run = squared
    ? runFixes(setting, readPlan(setting, squared.pog), FIX_ORDERS[0], async () => {
        await Promise.all([...steps].map((each) => each()));
      }).then((led) => ({
        reading: led.reading,
        kept: [{ kind: "vertical" } as const, ...led.kept],
      }))
    : Promise.resolve(null);
  const entry = { run, steps };
  squaredFirstRuns.set(start, entry);
  run.catch(() => {
    if (squaredFirstRuns.get(start) === entry) squaredFirstRuns.delete(start);
  });
  return run;
};

/**
 * Fix vertical blocking once fixes are made: the plan from before them squared, and the cards
 * fixed again on it, where that ends better than the plan on screen. Fixing the cards first can
 * leave less to square, so squaring the plan as it stands may fall short of what clicking Fix
 * vertical blocking first would have reached.
 */
export const squaredBeforeFixesOf = async (
  setting: FixSetting,
  unfixed: Pog,
  standing: PlanReading,
  pause: () => Promise<void> = () => new Promise((resolve) => setTimeout(resolve, 0)),
): Promise<PlanFix | null> => {
  const run = await squaredFirstRun(setting, readPlan(setting, unfixed), pause);
  return run && raisesTile(run.reading.verdict, standing.verdict)
    ? { pog: run.reading.pog, verdict: run.reading.verdict }
    : null;
};

/**
 * Every fix the studio knows, worked from each of `starts` in each of `FIX_ORDERS`, and the
 * best end kept by `endsBetter`: the plan on screen first, then any plan the caller would also
 * have worked (the panel's plan from before its fixes, as a fix made before can stand in the
 * way of a better end). Where Fix vertical blocking is offered on a start, its squared plan is
 * worked cards first too, as a user clicking it first would. A start whose cards-first end has
 * no card left and every check passed is not worked in the other orders. `onStep` counts every
 * fix tried; no total is known ahead, as a card is tried again each time the plan changes
 * under it.
 */
export const fixAll = async (
  setting: FixSetting,
  starts: readonly Pog[],
  onStep: (step: number) => void = () => {},
  pause: () => Promise<void> = () => new Promise((resolve) => setTimeout(resolve, 0)),
): Promise<FixAllResult> => {
  let tried = 0;
  const step = async () => {
    tried += 1;
    onStep(tried);
    await pause();
  };
  const [first, ...others] = FIX_ORDERS;
  let best: FixRun | null = null;
  for (const pog of starts) {
    const start = readPlan(setting, pog);
    const runs = [await runFixes(setting, start, first, step)];
    const squaredFirst = await squaredFirstRun(setting, start, step);
    if (squaredFirst) runs.push(squaredFirst);
    const [cardsFirst] = runs;
    const done =
      cardsFirst.reading.flags.length === 0 && cardsFirst.reading.verdict.failedGates.length === 0;
    if (!done && leadsElsewhere(setting, start)) {
      for (const order of others) runs.push(await runFixes(setting, start, order, step));
    }
    for (const run of runs) if (!best || endsBetter(run, best)) best = run;
  }
  if (!best) throw new Error("Fix all needs a plan to start from");
  return { pog: best.reading.pog, after: best.reading.verdict, kept: best.kept };
};

interface SharedFixAll {
  starts: readonly Pog[];
  run: Promise<FixAllResult>;
  /** The progress of every caller waiting on the run. */
  onSteps: Set<(step: number) => void>;
}

const fixAllRuns = new WeakMap<FixSetting, readonly SharedFixAll[]>();

/** `fixAll`, worked once for each setting and starts: a tree move weighs what Fix all reaches
 *  with it and without it, and Fix all clicked after reads the end already found. Each caller's
 *  `onStep` is told every fix tried from when it asks. A run that fails is not kept: the next
 *  caller works it again. */
export const fixAllOnce = (
  setting: FixSetting,
  starts: readonly Pog[],
  onStep?: (step: number) => void,
  pause?: () => Promise<void>,
): Promise<FixAllResult> => {
  const known = fixAllRuns.get(setting) ?? [];
  const same = known.find(
    (entry) =>
      entry.starts.length === starts.length && entry.starts.every((pog, at) => pog === starts[at]),
  );
  if (same) {
    if (onStep) same.onSteps.add(onStep);
    return same.run;
  }
  const onSteps = new Set(onStep ? [onStep] : []);
  const run = fixAll(setting, starts, (step) => onSteps.forEach((each) => each(step)), pause);
  const entry: SharedFixAll = { starts, run, onSteps };
  fixAllRuns.set(setting, [...known, entry]);
  run.catch(() => {
    const now = fixAllRuns.get(setting) ?? [];
    fixAllRuns.set(
      setting,
      now.filter((other) => other !== entry),
    );
  });
  return run;
};
